import CtaArrow from "@/components/svg/CtaArrow";
import { getHighlightClassName } from "@/helpers";
import { isFilled } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";


/**
 * @typedef {import("@prismicio/client").Content.FooterMainSlice} FooterMainSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<FooterMainSlice>} FooterMainProps
 * @type {import("react").FC<FooterMainProps>}
 */
const FooterMain = ({ slice }) => {
  const { logo, text, links, contact } = slice.primary;

  return (
    <footer
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-bleu px-5 pt-10 pb-15 text-beige-30 md:px-22 md:py-22"
    >
      <div className="flex flex-col gap-10 framing-xl lg:flex-row items-start justify-between">
        <div className="flex flex-col gap-7.5 md:max-w-[296px] md:gap-7.5">
          {isFilled.image(logo) && (
            <PrismicNextImage
              field={logo}
              className="h-[30px] w-[182px] object-contain object-left"
              fallbackAlt=""
            />
          )}

          {isFilled.richText(text) && (
            <PrismicRichText
              field={text}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-beige-30 p-sm">
                    {children}
                  </p>
                ),
              }}
            />
          )}
          <div className="flex flex-row gap-2.5">
            <a href="https://www.linkedin.com/company/aztk-cloud" aria-label="LinkedIn">
              <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="25" height="25" rx="5" fill="#335D74" />
                <path d="M21 13.8096V20H17.3562V14.224C17.3562 12.7727 16.829 11.7827 15.5105 11.7827C14.5037 11.7827 13.9041 12.4505 13.6409 13.0956C13.5447 13.3263 13.52 13.6477 13.52 13.9706V20H9.87449C9.87449 20 9.92389 10.2181 9.87449 9.20386H13.52V10.7341L13.4961 10.7693H13.52V10.7341C14.0038 10 14.8683 8.9505 16.8043 8.9505C19.202 8.9505 21 10.4941 21 13.8096ZM6.06208 4C4.81597 4 4 4.80537 4 5.86493C4 6.90101 4.79212 7.7307 6.01438 7.7307H6.03823C7.30989 7.7307 8.09945 6.90101 8.09945 5.86493C8.07731 4.80537 7.30989 4 6.06208 4ZM4.21634 20H7.86011V9.20386H4.21634V20Z" fill="white" />
              </svg>
            </a>
            <a href="https://www.instagram.com/aztk.io/" aria-label="Instagram">
              <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="25" height="25" rx="5" fill="#335D74" />
                <path d="M15.6088 4.91113H9.39025C8.17517 4.91256 7.01027 5.39594 6.15114 6.25521C5.29202 7.11447 4.80884 8.27945 4.80762 9.49454L4.80762 15.7131C4.80905 16.9282 5.29242 18.0931 6.15169 18.9522C7.01096 19.8113 8.17594 20.2945 9.39102 20.2957H15.6096C16.8247 20.2943 17.9896 19.8109 18.8487 18.9517C19.7078 18.0924 20.191 16.9274 20.1922 15.7123V9.49377C20.1908 8.27868 19.7074 7.11378 18.8482 6.25466C17.9889 5.39554 16.8239 4.91236 15.6088 4.91113V4.91113ZM18.6449 15.7123C18.6449 16.111 18.5664 16.5058 18.4138 16.8742C18.2612 17.2426 18.0376 17.5773 17.7557 17.8592C17.4737 18.1411 17.139 18.3647 16.7707 18.5173C16.4023 18.6699 16.0075 18.7484 15.6088 18.7484H9.39025C8.58517 18.7482 7.81313 18.4283 7.24392 17.8589C6.67471 17.2896 6.35494 16.5174 6.35494 15.7123V9.49377C6.35514 8.68868 6.67511 7.91664 7.24446 7.34743C7.81381 6.77822 8.58594 6.45846 9.39102 6.45846H15.6096C16.4147 6.45866 17.1867 6.77862 17.7559 7.34798C18.3251 7.91733 18.6449 8.68945 18.6449 9.49454V15.7131V15.7123Z" fill="white" />
                <path d="M12.5002 8.62402C11.4456 8.62565 10.4347 9.04536 9.68904 9.79115C8.9434 10.5369 8.52389 11.5479 8.52246 12.6025C8.52368 13.6574 8.94321 14.6687 9.68904 15.4147C10.4349 16.1606 11.4461 16.5804 12.501 16.5818C13.556 16.5806 14.5674 16.1609 15.3134 15.4149C16.0594 14.6689 16.479 13.6575 16.4802 12.6025C16.4786 11.5477 16.0587 10.5365 15.3126 9.79083C14.5664 9.04515 13.5551 8.62581 12.5002 8.62479V8.62402ZM12.5002 15.0345C11.8554 15.0345 11.237 14.7783 10.7811 14.3224C10.3252 13.8665 10.069 13.2481 10.069 12.6033C10.069 11.9585 10.3252 11.3401 10.7811 10.8842C11.237 10.4283 11.8554 10.1721 12.5002 10.1721C13.145 10.1721 13.7634 10.4283 14.2193 10.8842C14.6752 11.3401 14.9314 11.9585 14.9314 12.6033C14.9314 13.2481 14.6752 13.8665 14.2193 14.3224C13.7634 14.7783 13.145 15.0345 12.5002 15.0345V15.0345Z" fill="white" />
                <path d="M16.4864 9.60758C17.0129 9.60758 17.4396 9.18082 17.4396 8.65438C17.4396 8.12794 17.0129 7.70117 16.4864 7.70117C15.96 7.70117 15.5332 8.12794 15.5332 8.65438C15.5332 9.18082 15.96 9.60758 16.4864 9.60758Z" fill="white" />
              </svg>
            </a>
          </div>

        </div>

        {links.length > 0 && (
          <div className="flex flex-row justify-start md:justify-end items-end gap-5 md:gap-15 ">
            {links.map((item, index) => {
              return (
                <div key={`footer-link-${index}`} className="flex flex-col gap-5 flex-1">
                  <div className={`h3 leading-none uppercase text-beige-30 w-fit ${getHighlightClassName("bleu-50", "logo")}`}>
                    {item.label}
                  </div>
                  {
                    item.link.map((link, linkIndex) => {
                      return (
                        <div key={`footer-link-${index}-${linkIndex}`} className="w-[165px] flex flex-col gap-5 footer-nav">
                          <PrismicNextLink field={link} prefetch={false} />
                        </div>
                      )
                    })
                  }
                </div>
              )
            }
            )}
          </div>
        )}

        {
          contact.length > 0 && (
            <div className="flex flex-col gap-5">
              {contact.map((item, index) => {
                return (
                  <div key={`footer-link-${index}`} className="flex flex-col gap-5">
                    <div className={`h3 leading-none uppercase text-beige-30 w-fit ${getHighlightClassName("bleu-50", "logo")}`}>
                      {item.label}
                    </div>
                    {
                      item.link.map((link, linkIndex) => {
                        if (link.variant === "Tel") {
                          return (
                            <div key={`footer-link-${index}-${linkIndex}`} className="flex flex-col gap-5 cta-text">
                              <a href={`tel:${link.text}`}>
                                {link.text}
                              </a>
                            </div>
                          )
                        }
                        if (link.variant === "Email") {
                          return (
                            <div key={`footer-link-${index}-${linkIndex}`} className="flex flex-col gap-5 cta-text">
                              <a href={`mailto:${link.text}`}>
                                {link.text}
                              </a>
                            </div>
                          )
                        }
                        if (link.variant === "Primary") {
                          return (
                            <div key={`footer-link-${index}-${linkIndex}`} className="flex flex-col gap-5 cta-text">
                              <PrismicNextLink field={link} prefetch={false} />
                            </div>
                          )
                        }
                        if (link.variant === "Secondary") {
                          return (
                            <PrismicNextLink
                              key={link.key ?? index}
                              field={link}
                              className={`w-full md:w-auto cta cta-text flex flex-row gap-5 bg-bleu-50 text-blanc`}
                              prefetch={false}
                            >
                              {link.text}
                              <CtaArrow className={"text-blanc"} />
                            </PrismicNextLink>
                          )
                        }
                      })
                    }
                  </div>
                )
              })}
            </div>
          )
        }
      </div>
    </footer>
  );
};

export default FooterMain;
